import React, { useState } from "react";
import { fetchJson } from "../../lib/api";
import type { HouseInvite } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";

export const PendingInvites = ({
  invites,
  onAccepted,
}: {
  invites: HouseInvite[];
  onAccepted: (invite: HouseInvite) => Promise<void> | void;
}) => {
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);

  if (!invites.length) {
    return null;
  }

  const accept = async (invite: HouseInvite) => {
    setError(null);
    setProcessingId(invite.id);
    try {
      await fetchJson("/api/invites/" + invite.id + "/accept", { method: "POST" });
      await onAccepted(invite);
    } catch (acceptError) {
      setError(
        acceptError instanceof Error ? acceptError.message : t("invite.acceptFailed", "Failed to accept"),
      );
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("invite.pendingHouseInvites", "Pending house invites")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {invites.map((invite) => (
          <div
            key={invite.id}
            className="flex items-center justify-between rounded-md border brand-border p-3"
          >
            <div>
              <p className="text-sm font-medium text-ink">{invite.houseName}</p>
              <p className="text-xs text-muted">
                {t("invite.roleLabel", "Role")}:{" "}
                {invite.role === "full"
                  ? t("invite.full", "Full access")
                  : t("invite.readonly", "Read-only")}{" "}
                · {invite.email}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => accept(invite)}
              disabled={processingId === invite.id}
            >
              {processingId === invite.id
                ? t("invite.accepting", "Accepting...")
                : t("invite.accept", "Accept")}
            </Button>
          </div>
        ))}
        {error ? <p className="text-sm text-error">{error}</p> : null}
      </CardContent>
    </Card>
  );
};
